import type {ConferenceApiModel} from '../../../api/conference';
import type {CourseApiModel} from '../../../api/course';
import type {Nullable} from '../../utility-types';

import {currentUserStore} from '../../../api/user';
import {allOf, not} from '../../conditionalUtils';
import {isNullish} from '../../typecheck';
import {
  isCourseCorporate,
  isCourseEditable,
  isCourseIndividual,
} from '../course';
import {isMeCoachForEntity} from '../isMeCoachForEntity';
import {isConferenceEnded} from './isConferenceEnded';
import {isConferenceExpired} from './isConferenceExpired';
import {isConferenceRescheduleEnabled} from './isConferenceRescheduleEnabled';
import {isConferenceStarted} from './isConferenceStarted';
import {isConferenceStartEnabled} from './isConferenceStartEnabled';
import {isIntroCall as isIntroCallUtil} from './isIntroCall';

type Action = 'cancel' | 'delete' | 'edit' | 'join' | 'reschedule' | 'start';

type ConferenceApiModelWithCourse = ConferenceApiModel & {
  course?: Nullable<CourseApiModel>;
};

export const getConferenceActions = (
  conference?: Nullable<ConferenceApiModelWithCourse>,
) => {
  const {user} = currentUserStore;

  if (isNullish(conference) || isNullish(user)) {
    return [];
  }

  const actions: Action[] = [];

  const isMeCoach = isMeCoachForEntity(conference);
  const isIntroCall = isIntroCallUtil(conference);
  const isStarted = isConferenceStarted(conference);
  const isAbleToStart = isConferenceStartEnabled(conference);
  const isInIndividualCourse = isCourseIndividual(conference.course);
  const isConferenceCorporate = isCourseCorporate(conference.course);
  const isEnded = isConferenceEnded(conference);
  const isExpired = isConferenceExpired(conference);
  const isRescheduled = conference.status === 'rescheduled';
  const isCanceled = conference.status === 'cancelled';

  // means that creation of the conference decreased the number of available sessions
  // TODO: give more meaningful name
  const isFromMyAvailableConferences = conference.client_id === user._id;

  if (isEnded || isExpired || isRescheduled || isCanceled) {
    return actions;
  }

  if (isMeCoach) {
    if (isAbleToStart && !isStarted) {
      actions.push('start');
    } else if (isStarted) {
      actions.push('join');
    }
  } else if (isAbleToStart || isStarted) {
    actions.push('join');
  }

  const isAnyChangesAllowed = allOf([isStarted, isEnded, isExpired], false);

  if (not(isAnyChangesAllowed)) {
    return actions;
  }

  if (isIntroCall) {
    actions.push(isMeCoach ? 'delete' : 'cancel');

    return actions;
  }

  if (!isCourseEditable(conference.course)) {
    return actions;
  }

  if (isMeCoach) {
    if (!isConferenceCorporate) {
      actions.push('edit');
    }

    actions.push('delete');
  } else if (isInIndividualCourse && isFromMyAvailableConferences) {
    if (isConferenceRescheduleEnabled(conference)) {
      actions.push('reschedule');
    }

    actions.push('cancel');
  }

  return actions;
};
